import React, { useState, useEffect, useContext } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import { AppContext } from '../../App';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import PageSeo from '../../components/PageSeo';
import { 
  ChevronRight, Building2, MapPin, ArrowLeft, Target, Construction, Home, 
  Layers, Square, Anchor, Droplets, Syringe, FileText, ClipboardCheck, 
  AlertTriangle, Gauge, FlaskConical, Activity, Wrench, Circle, Cog, 
  Container, Hammer, User, HardHat, Briefcase, Users
} from 'lucide-react';

// Icon mapping for categories
const CATEGORY_ICONS = {
  'entreprises-micropieux': Target,
  'entreprises-pieux-fores': Construction,
  'reprise-sous-oeuvre': Home,
  'paroi-berlinoise-parisienne': Layers,
  'paroi-moulee-barrette': Square,
  'palplanches-foncage': Layers,
  'tirants-ancrage-cloutage': Anchor,
  'injections-jet-grouting': Droplets,
  'injections-resine-expansive': Syringe,
  'colonnes-ballastees-inclusions': Construction,
  'bureaux-etudes-g1-g2': FileText,
  'bureaux-etudes-g3-g4': ClipboardCheck,
  'diagnostics-g5-sinistres': AlertTriangle,
  'essais-pressiometriques-penetrometriques': Gauge,
  'laboratoires-essais-sols': FlaskConical,
  'instrumentation-monitoring': Activity,
  'foreuses-occasion-micropieux': Wrench,
  'foreuses-gros-diametre': Circle,
  'machines-essais-occasion': Gauge,
  'outils-consommables-forage': Cog,
  'centrales-boue-injection': Container,
  'vibrofonceurs-marteaux': Hammer,
  'ingenieurs-geotechniciens-independants': User,
  'chefs-chantier-foreurs-freelance': HardHat,
  'cabinets-recrutement-geotechnique': Briefcase
};

const FAMILY_COLORS = {
  'travaux-fondations-speciales': { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200', icon: 'text-blue-600' },
  'ingenierie-etudes-geotechniques': { bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-200', icon: 'text-green-600' },
  'materiel-equipements-forage': { bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-200', icon: 'text-orange-600' },
  'conseil-recrutement-freelance': { bg: 'bg-purple-50', text: 'text-purple-700', border: 'border-purple-200', icon: 'text-purple-600' }
};

// Static data for SSG pre-rendering (will be replaced by API data on client)
const STATIC_FAMILIES = {
  'travaux-fondations-speciales': {
    name_fr: 'Travaux de Fondations Spéciales',
    description_fr: 'Entreprises spécialisées dans les travaux de fondations spéciales : micropieux, pieux forés, parois moulées, tirants, injections et techniques de renforcement des sols.',
    categories: [
      { slug: 'entreprises-micropieux', name_fr: 'Entreprises de micropieux', description_fr: 'Réalisation de micropieux pour fondations profondes' },
      { slug: 'entreprises-pieux-fores', name_fr: 'Entreprises de pieux forés', description_fr: 'Exécution de pieux forés de tout diamètre' },
      { slug: 'reprise-sous-oeuvre', name_fr: 'Reprise en sous-œuvre et stabilisation', description_fr: 'Renforcement et reprise de fondations existantes' },
      { slug: 'paroi-berlinoise-parisienne', name_fr: 'Parois berlinoises et parisiennes', description_fr: 'Soutènements provisoires et définitifs' },
      { slug: 'paroi-moulee-barrette', name_fr: 'Parois moulées et barrettes', description_fr: 'Réalisation de parois moulées et barrettes' },
      { slug: 'palplanches-foncage', name_fr: 'Palplanches et fonçage', description_fr: 'Installation de palplanches et rideux' },
      { slug: 'tirants-ancrage-cloutage', name_fr: 'Tirants, ancrage et cloutage', description_fr: 'Mise en place de tirants et ancrages' },
      { slug: 'injections-jet-grouting', name_fr: 'Injections et jet grouting', description_fr: 'Amélioration des sols par injection' },
      { slug: 'injections-resine-expansive', name_fr: 'Injections de résine expansive', description_fr: 'Stabilisation par résine expansive' },
      { slug: 'colonnes-ballastees-inclusions', name_fr: 'Colonnes ballastées et inclusions rigides', description_fr: 'Renforcement de sols par colonnes' }
    ]
  },
  'ingenierie-etudes-geotechniques': {
    name_fr: 'Ingénierie & Études Géotechniques',
    description_fr: 'Bureaux d\'études géotechniques, laboratoires d\'essais sols et entreprises d\'instrumentation pour vos projets de construction.',
    categories: [
      { slug: 'bureaux-etudes-g1-g2', name_fr: 'Bureaux d\'études géotechniques G1 & G2', description_fr: 'Études préliminaires et avant-projet' },
      { slug: 'bureaux-etudes-g3-g4', name_fr: 'Bureaux d\'études géotechniques G3 & G4', description_fr: 'Études d\'exécution et suivi' },
      { slug: 'diagnostics-g5-sinistres', name_fr: 'Diagnostics G5 et sinistres', description_fr: 'Expertise et diagnostic géotechnique' },
      { slug: 'essais-pressiometriques-penetrometriques', name_fr: 'Essais pressiométriques et pénétrométriques', description_fr: 'Essais in situ pour caractérisation des sols' },
      { slug: 'laboratoires-essais-sols', name_fr: 'Laboratoires d\'essais sols', description_fr: 'Analyses et essais de laboratoire' },
      { slug: 'instrumentation-monitoring', name_fr: 'Instrumentation et monitoring', description_fr: 'Surveillance et instrumentation géotechnique' }
    ]
  },
  'materiel-equipements-forage': {
    name_fr: 'Matériel & Équipements de Forage',
    description_fr: 'Achat, vente et location de foreuses, machines d\'essais, outils de forage et équipements pour travaux géotechniques.',
    categories: [
      { slug: 'foreuses-occasion-micropieux', name_fr: 'Foreuses d\'occasion micropieux', description_fr: 'Foreuses pour micropieux et petits diamètres' },
      { slug: 'foreuses-gros-diametre', name_fr: 'Foreuses gros diamètre', description_fr: 'Foreuses pour pieux de grand diamètre' },
      { slug: 'machines-essais-occasion', name_fr: 'Machines d\'essais d\'occasion', description_fr: 'Équipements pour essais géotechniques' },
      { slug: 'outils-consommables-forage', name_fr: 'Outils et consommables de forage', description_fr: 'Outils, taillants et consommables' },
      { slug: 'centrales-boue-injection', name_fr: 'Centrales à boue et injection', description_fr: 'Centrales de malaxage et injection' },
      { slug: 'vibrofonceurs-marteaux', name_fr: 'Vibrofonceurs et marteaux de battage', description_fr: 'Matériel de fonçage et battage' }
    ]
  },
  'conseil-recrutement-freelance': {
    name_fr: 'Conseil, Recrutement & Freelance',
    description_fr: 'Consultants indépendants, ingénieurs freelance et cabinets de recrutement spécialisés dans le secteur géotechnique.',
    categories: [
      { slug: 'ingenieurs-geotechniciens-independants', name_fr: 'Ingénieurs géotechniciens indépendants', description_fr: 'Consultants et experts indépendants' },
      { slug: 'chefs-chantier-foreurs-freelance', name_fr: 'Chefs de chantier et foreurs freelance', description_fr: 'Professionnels de terrain indépendants' },
      { slug: 'cabinets-recrutement-geotechnique', name_fr: 'Cabinets de recrutement géotechnique', description_fr: 'Recrutement spécialisé géotechnique' }
    ]
  }
};

export default function DirectoryFamily() {
  const { familySlug } = useParams();
  const { API } = useContext(AppContext);
  
  // Use static data as initial state for SSG
  const staticFamily = STATIC_FAMILIES[familySlug] || null;
  const [family, setFamily] = useState(staticFamily);
  const [loading, setLoading] = useState(!staticFamily);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchFamily = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${API}/directory/families/${familySlug}`);
        setFamily(response.data);
      } catch (err) {
        console.error('Error fetching family:', err);
        setError('Famille non trouvée');
      } finally {
        setLoading(false);
      }
    };
    fetchFamily();
  }, [API, familySlug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#635BFF]"></div>
      </div>
    );
  }

  if (error || !family) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-800 mb-4">{error || 'Famille non trouvée'}</h1>
          <Link to="/annuaire">
            <Button>Retour à l'annuaire</Button>
          </Link>
        </div>
      </div>
    );
  }

  const colors = FAMILY_COLORS[familySlug] || FAMILY_COLORS['travaux-fondations-speciales'];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <PageSeo 
        title={family.meta_title_fr || `${family.name_fr} | Annuaire GEOTS`}
        description={family.meta_description_fr || family.description_fr}
        canonical={`/annuaire/${familySlug}`}
      />
      
      {/* Breadcrumb & Hero */}
      <div className={`${colors.bg} border-b ${colors.border}`}>
        <div className="container mx-auto px-4 py-8">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-sm mb-6">
            <Link to="/annuaire" className="text-gray-500 hover:text-gray-700 flex items-center gap-1">
              <ArrowLeft className="w-4 h-4" />
              Annuaire
            </Link>
            <ChevronRight className="w-4 h-4 text-gray-400" />
            <span className={colors.text + ' font-medium'}>{family.name_fr}</span>
          </nav>
          
          <div className="max-w-4xl">
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#0A2540] mb-4">
              {family.name_fr}
            </h1>
            <p className="text-lg text-gray-600 mb-6">
              {family.description_fr}
            </p>
            
            <div className="flex flex-wrap gap-4">
              <div className={`inline-flex items-center gap-2 ${colors.bg} ${colors.text} px-4 py-2 rounded-lg border ${colors.border}`}>
                <Building2 className="w-5 h-5" />
                <span className="font-medium">{family.categories?.length || 0} sous-catégories</span>
              </div>
              <div className={`inline-flex items-center gap-2 ${colors.bg} ${colors.text} px-4 py-2 rounded-lg border ${colors.border}`}>
                <Users className="w-5 h-5" />
                <span className="font-medium">{family.professional_count || 0} entreprises</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Categories Grid */}
      <div className="container mx-auto px-4 py-12">
        <h2 className="text-2xl font-bold text-[#0A2540] mb-8">
          Choisissez une spécialité
        </h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {family.categories?.map(category => {
            const IconComponent = CATEGORY_ICONS[category.slug] || Building2;
            
            return (
              <Link 
                key={category.id} 
                to={`/annuaire/${familySlug}/${category.slug}`}
                className="group"
              >
                <Card className={`h-full border-2 ${colors.border} hover:border-[#635BFF] hover:shadow-lg transition-all duration-200`}>
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className={`p-3 ${colors.bg} rounded-xl ${colors.icon}`}>
                        <IconComponent className="w-6 h-6" />
                      </div>
                      
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-[#0A2540] mb-2 group-hover:text-[#635BFF] transition-colors">
                          {category.name_fr}
                        </h3>
                        <p className="text-sm text-gray-600 line-clamp-2 mb-3">
                          {category.description_fr}
                        </p>
                        
                        <div className="flex items-center justify-between">
                          <span className={`text-sm ${colors.text} font-medium`}>
                            {category.professional_count || 0} entreprise{(category.professional_count || 0) !== 1 ? 's' : ''}
                          </span>
                          <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-[#635BFF] group-hover:translate-x-1 transition-all" />
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            );
          })}
        </div>
      </div>

      {/* SEO Content Section */}
      {family.seo_content_fr && (
        <div className="bg-white border-t">
          <div className="container mx-auto px-4 py-12">
            <div className="prose prose-lg max-w-4xl mx-auto" dangerouslySetInnerHTML={{ __html: family.seo_content_fr }} />
          </div>
        </div>
      )}

      {/* CTA Section */}
      <div className={`${colors.bg} border-t ${colors.border} py-12`}>
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-2xl font-bold text-[#0A2540] mb-4">
            Votre entreprise est spécialisée dans ce domaine ?
          </h2>
          <p className="text-gray-600 mb-6 max-w-xl mx-auto">
            Inscrivez-vous gratuitement dans l'annuaire et apparaissez dans les résultats de recherche.
          </p>
          <Link to="/directory/submit">
            <Button size="lg" className="bg-[#635BFF] hover:bg-[#5248d9]">
              Inscrire mon entreprise gratuitement
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}
